// Whether the dashboard's empty state should send the user to guided creation
// on the landing page instead of quick-creating a project.
//
// The redirect is an ONBOARDING affordance: someone who owns nothing yet is
// better served by the prompt-first homepage than by an empty canvas. It keys
// off `pagination.total === 0` from the dashboard list query — and since
// RFC-1371 that total is SCOPED, by active team and by the Private/Team
// switch. It is not a count of everything the account owns.
//
// That is how the redirect started firing on an ordinary, non-onboarding path.
// A freshly created team is empty by construction, so "+ New Project" inside
// one bounced the user to the marketing homepage without creating anything,
// while the same button worked after switching back to Personal — which reads
// as "teams are broken" rather than as an onboarding nudge.
//
// So the emptiness has to be attributable to the ACCOUNT, not to the scope.
// An account with no team row at all (teams off, or not yet provisioned) is
// unscoped and keeps the original behaviour. Inside teams, only the user's OWN
// personal team on the default Private half can stand in for "this account has
// nothing" — every other combination is a filter the user applied, and an
// empty answer to a filter is not an onboarding signal.

import type { ProjectVisibility } from './VisibilitySwitch'

export interface GuidedCreationRedirectInputs {
  /** `pagination.total` for the CURRENT scope, not for the account. */
  scopedTotal: number
  /**
   * The resolved active team. Null when teams are off (every teams query is
   * gated on the flag, so the list stays empty) and for an account that
   * predates personal-team provisioning.
   */
  activeTeamId: string | null
  /** The personal team the user OWNS. Null if provisioning has not run. */
  personalTeamId: string | null
  /** The selected half of the Private/Team switch. */
  visibility: ProjectVisibility
}

export function shouldRedirectToGuidedCreation(i: GuidedCreationRedirectInputs): boolean {
  // A stale or out-of-range page can have no visible cards even though the
  // scope holds projects. That is an experienced user, so quick-create.
  if (i.scopedTotal !== 0) return false

  // No team scoping the list: the total really is the account's.
  if (i.activeTeamId === null) return true

  // Someone else's team, or one the user created — empty says nothing about
  // whether they are new here. `personalTeamId` being null lands here too,
  // which is right: without a known personal team there is no scope that can
  // claim to speak for the account.
  if (i.activeTeamId !== i.personalTeamId) return false

  // Own personal team. Only the Private half is the unfiltered view; an empty
  // Team half is just a filter that matched nothing.
  return i.visibility === 'private'
}
